import {
	createVestingTermLeaverProvision,
	deleteVestingTermLeaverProvision,
	updateVestingTermLeaverProvision,
	UpdateLeaverProvisionArgs,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	createOrganizationVestingTermLeaverProvision,
	deleteOrganizationVestingTermLeaverProvision,
	updateOrganizationVestingTermLeaverProvision,
} from '@dealstack/frontend/shared/dataExchange/services/programme-defaults-vesting.service';
import {
	CreateVestingTermLeaverProvisionDto,
	UpdateVestingTermLeaverProvisionDto,
} from '@dealstack/shared/lib/generated';
import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

export const useLeaverProvisionMutations = (
	refetchVestingTerms: () => Promise<unknown>,
	organizationId?: string
) => {
	const createVestingTermLeaverProvisionRequest = useMutation({
		mutationFn: (args: CreateVestingTermLeaverProvisionDto) =>
			organizationId
				? createOrganizationVestingTermLeaverProvision(organizationId, args)
				: createVestingTermLeaverProvision(args),
	});
	const updateVestingTermLeaverProvisionRequest = useMutation({
		mutationFn: (args: UpdateLeaverProvisionArgs) =>
			organizationId
				? updateOrganizationVestingTermLeaverProvision(organizationId, args)
				: updateVestingTermLeaverProvision(args),
	});
	const deleteVestingTermLeaverProvisionRequest = useMutation({
		mutationFn: (args: { leaverProvisionId: string }) =>
			organizationId
				? deleteOrganizationVestingTermLeaverProvision(organizationId, args)
				: deleteVestingTermLeaverProvision(args),
	});

	const onCreateVestingTermLeaverProvision = useCallback(
		(dto: CreateVestingTermLeaverProvisionDto) => {
			createVestingTermLeaverProvisionRequest.mutate(dto, {
				onSuccess: async () => {
					await refetchVestingTerms();
				},
			});
		},
		[createVestingTermLeaverProvisionRequest, refetchVestingTerms]
	);

	const onUpdateVestingTermLeaverProvision = useCallback(
		(leaverProvisionId: string, formValues: UpdateVestingTermLeaverProvisionDto) => {
			updateVestingTermLeaverProvisionRequest.mutate(
				{ leaverProvisionId, ...formValues },
				{
					onSuccess: async () => {
						await refetchVestingTerms();
					},
				}
			);
		},
		[updateVestingTermLeaverProvisionRequest, refetchVestingTerms]
	);

	const onDeleteVestingTermLeaverProvision = useCallback(
		(leaverProvisionId: string) => {
			deleteVestingTermLeaverProvisionRequest.mutate(
				{ leaverProvisionId },
				{
					onSuccess: async () => {
						await refetchVestingTerms();
					},
				}
			);
		},
		[deleteVestingTermLeaverProvisionRequest, refetchVestingTerms]
	);

	return useMemo(
		() => ({
			onCreateVestingTermLeaverProvision,
			onUpdateVestingTermLeaverProvision,
			onDeleteVestingTermLeaverProvision,
		}),
		[
			onCreateVestingTermLeaverProvision,
			onUpdateVestingTermLeaverProvision,
			onDeleteVestingTermLeaverProvision,
		]
	);
};
